import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { ConfirmModal } from "../../../../../controls/modals/ConfirmModal";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import {
  CATALOG_ELEMENT_INITIAL_VALUES,
  CatalogElement,
} from "../../../models/CatalogElement";
import { validateNoSpecialChars } from "../../../validates";
import { useUpsertCatalogElementMutation } from "../hooks/mutations/useUpsertCatalogElementMutation";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  element: CatalogElement | null;
  elements: CatalogElement[] | null;
  showModal: "new" | "edit" | "delete" | "";
  onHide: ({ entityId, shouldUpdate }: onModalHideProps) => void;
};
export const ElementModal = ({
  element,
  elements,
  showModal,
  onHide,
}: Props) => {
  const upsertMutation = useUpsertCatalogElementMutation();

  const [notificationMessage, setNotificationMessage] = useState("");
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [existingElement, setExistingElement] = useState("");
  const [title, setTitle] = useState("");

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CatalogElement>({
    defaultValues: CATALOG_ELEMENT_INITIAL_VALUES,
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || showModal == "new";

  const onSubmit: SubmitHandler<CatalogElement> = async (
    data: CatalogElement,
  ) => {
    const allElements: CatalogElement[] = elements || [];
    if (showModal !== "delete") {
      if (
        allElements.some(
          (el) =>
            el.ElementName === data.ElementName &&
            el.ElementId !== data.ElementId,
        )
      ) {
        setExistingElement(data.ElementName);
        setConfirmOpen(true);
      } else {
        await performUpsert(data);
      }
    }
  };

  const performUpsert = async (data: CatalogElement) => {
    try {
      const resp = await upsertMutation.mutateAsync(data);
      const entityId = get(resp, "data.tables[0].rows[0].Id", 0);
      setNotificationMessage("The element was saved successfully");
      onHide({ entityId: entityId, shouldUpdate: true });
    } catch (err: any) {
      setError(err.message);
      onHide({ shouldUpdate: false });
    }
  };

  useEffect(() => {
    if (showModal != "") {
      setTitle(showModal.toUpperCase());
      reset(element || CATALOG_ELEMENT_INITIAL_VALUES);
    }
  }, [showModal]);

  return (
    <>
      <ConfirmModal
        show={confirmOpen}
        onHide={(proceed: boolean) => {
          if (proceed) {
            performUpsert(getValues());
            onHide({ shouldUpdate: true });
          }
          setConfirmOpen(false);
        }}
        existingName={existingElement}
        label="Element"
      />
      <MesfModal
        title={`${title} ELEMENT`}
        open={showModal != ""}
        handleClose={() => onHide({ shouldUpdate: false })}
        id="element-modal"
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={12}>
                <Controller
                  name="ElementName"
                  control={control}
                  rules={{
                    required: "Name is required",
                    validate: validateNoSpecialChars,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      label="Name"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="Decimals"
                  control={control}
                  rules={{
                    required: "Decimals are required",
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="# Decimals"
                      value={field.value}
                      onChange={field.onChange}
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      type="number"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      inputProps={{
                        style: {
                          textAlign: "right",
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              {showModal !== "new" && (
                <Grid item xs={12} md={4}>
                  <Controller
                    name="Order"
                    control={control}
                    rules={{
                      required: "Order are required",
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Order"
                        value={field.value}
                        onChange={field.onChange}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        type="number"
                        autoComplete="off"
                        error={!!error}
                        helperText={error?.message}
                        inputProps={{
                          style: {
                            textAlign: "right",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <Controller
                  name="IsActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        margin: 0,
                        height: "100%",
                      }}
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Enabled"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={upsertMutation.isLoading}
              onHide={() => onHide({ shouldUpdate: false })}
              buttonLabel={showModal === "delete" ? "Delete" : "Save"}
              buttonColor={showModal === "delete" ? "secondary" : "primary"}
              disabled={!isFormDirty && showModal !== "delete"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error with Element"
      />
    </>
  );
};
